import type { ResponseFn } from '../engine/types'

interface ResponseCurveProps {
  responseFn: ResponseFn
  currentStimulus?: number
  color: string
  width?: number
  height?: number
  domainMaxStimulus?: number
}

export function ResponseCurve({
  responseFn,
  currentStimulus,
  color,
  width = 200,
  height = 100,
  domainMaxStimulus = 1,
}: ResponseCurveProps) {
  const pad = { top: 8, right: 8, bottom: 22, left: 28 }
  const plotW = width - pad.left - pad.right
  const plotH = height - pad.top - pad.bottom
  const effectiveMaxStimulus = Math.max(
    domainMaxStimulus,
    currentStimulus ?? 0,
    Number.EPSILON,
  )

  // Sample the response function
  const steps = 60
  const points: { x: number; y: number }[] = []
  let maxY = 0
  for (let i = 0; i <= steps; i++) {
    const s = (i / steps) * effectiveMaxStimulus
    const y = responseFn(s)
    if (y > maxY) maxY = y
    points.push({ x: s, y })
  }
  if (maxY === 0) maxY = 1

  const toSvgX = (s: number) => pad.left + (s / effectiveMaxStimulus) * plotW
  const toSvgY = (v: number) => pad.top + plotH - (v / maxY) * plotH
  const plottedStimulus =
    currentStimulus == null ? undefined : Math.max(0, currentStimulus)

  const pathD = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${toSvgX(p.x)},${toSvgY(p.y)}`)
    .join(' ')

  const mutedColor = 'var(--color-text-muted)'
  const borderColor = 'var(--color-border)'

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      style={{ display: 'block', maxWidth: '100%', height: 'auto' }}
      aria-label="Response curve"
    >
      {/* Axes */}
      <line
        x1={pad.left}
        y1={pad.top + plotH}
        x2={pad.left + plotW}
        y2={pad.top + plotH}
        stroke={borderColor}
        strokeWidth={1}
      />
      <line
        x1={pad.left}
        y1={pad.top}
        x2={pad.left}
        y2={pad.top + plotH}
        stroke={borderColor}
        strokeWidth={1}
      />

      {/* Curve */}
      <path d={pathD} fill="none" stroke={color} strokeWidth={1.5} />

      {/* Live dot */}
      {plottedStimulus != null && (
        <circle
          cx={toSvgX(plottedStimulus)}
          cy={toSvgY(responseFn(plottedStimulus))}
          r={4}
          fill={color}
        />
      )}

      {/* Axis labels */}
      <text
        x={pad.left + plotW / 2}
        y={height - 2}
        textAnchor="middle"
        fill={mutedColor}
        style={{ fontFamily: 'var(--font-ui)', fontSize: '10px' }}
      >
        stimulus
      </text>
      <text
        x={4}
        y={pad.top + plotH / 2}
        textAnchor="middle"
        fill={mutedColor}
        style={{
          fontFamily: 'var(--font-ui)',
          fontSize: '10px',
          writingMode: 'vertical-rl',
          transform: `rotate(180deg)`,
          transformOrigin: `4px ${pad.top + plotH / 2}px`,
        }}
      >
        speed
      </text>
    </svg>
  )
}
